/**
 * Works out what the edit-food page was opened for, from its navigation parameters.
 *
 * This lives on its own because two lifecycle hooks need the same answer and must not be able
 * to disagree: `ionViewCanEnter` uses it to decide which food to load (and to refuse the page
 * when that food cannot be shown), and `ngOnInit` uses it to set the page up. Before #4681 the
 * parsing existed once, inside `ngOnInit`; splitting the load out meant either duplicating it
 * or extracting it, and a duplicate that drifted would silently break creating a food from a
 * barcode scan.
 */

/** What the page should do: open an existing food, or start a new one. */
export interface EditFoodTarget {
  /** id of an existing food to load, or null when the page is creating a new food or recipe */
  id: string | null;
  /** barcode to pre-fill a newly created food with */
  ean?: string;
  /** name to pre-fill a newly created food with */
  name?: string;
}

/** The raw navigation parameters edit-food is opened with. */
export interface EditFoodNavParams {
  id?: string;
  ean?: string;
  name?: string;
}

/**
 * The `id` parameter carries two different things, which is why this needs explaining.
 *
 * Normally it is a food id. But the URL format only allows a fixed list of path parameters, and
 * an undefined one produces either a literal "null" or an empty path segment, both of which
 * break the URL parser. So when the page is opened to CREATE a food from a barcode or a name
 * — where there is no id yet — those two values are packed into the `id` parameter as a small
 * JSON object instead. A leading `{` is what tells the two apart.
 *
 * Throws if the id looks like that JSON object but cannot be parsed. That is a corrupt link, and
 * the caller turns it into a reported page-load failure rather than a page that half works.
 */
export function resolveEditFoodTarget(params: EditFoodNavParams): EditFoodTarget {
  let id = params?.id;
  let ean = params?.ean;
  let name = params?.name;

  if (id && id.startsWith('{')) {
    ({ ean, name } = JSON.parse(id));
    id = null;
  }

  // an empty id means "no id" — it reaches us as an empty path segment
  if (!id) {
    id = null;
  }

  return { id, ean, name };
}
